<template>
  <div>
    <app-alert-in-page v-if="isWitnessEnabled" class="mb-4">
      <div class="d-flex">
        <p>{{
            $t('project.signing-checklist.add.step2.Section2.witnessInfo.regular')
          }}
        </p>
        <p class="ml-1 body-md-bold">
          {{ $t('project.signing-checklist.add.step2.Section2.witnessInfo.bold') }}
        </p>
      </div>
       <a
         class="app-link learnMoreLink body-md-bold"
         href="https://help.closd.com"
         rel="noopener"
         target="_blank"
       >
          {{ $t('common.learnMore') }}
          <app-material-icon name="open_in_new" color="inherit" class="ml-1" size="xs" />
      </a>
    </app-alert-in-page>

    <v-row>
      <v-col cols="12">
        <div class="text-text-secondary">{{ $t('project.signing-checklist.add.step2.Section2.signatureTypeCaption') }}</div>
        <AppDownloadFile :link="learnMoreSignatureTypesLink" forceReader>
          <template #default="{ submitForm }">
            <a class="app-link learnMoreLink body-md-bold" @click="submitForm">
              {{ $t('project.signing-checklist.add.step2.Section2.learnMoreSignatureType') }}
              <app-material-icon name="open_in_new" color="inherit" size="xs" />
            </a>
          </template>
        </AppDownloadFile>
      </v-col>

      <v-col cols="12">
        <app-alert-in-page
          v-if="signatureTypeErrorMessages.length"
          variant="error"
          :dismissible="false"
          class="mb-4"
          data-locator="signature-type-error-alert"
        >
          <p v-for="message in signatureTypeErrorMessages" :key="message">
            {{ message }}
          </p>
        </app-alert-in-page>
        <app-tile-group
          :model-value="signatureTypeFormField.$model"
          :options="signatureTypeTileOptions"
          :disabled="isEditingLimited"
          :aria-label="$t('project.signing-checklist.add.step2.Section2.signatureTypeLabel')"
          data-locator="signature-type-tile-group"
          @update:model-value="signatureTypeChange"
        />
      </v-col>

      <v-col v-if="signatureTypeFormField.$model === SIGNATURE_TYPE_HANDWRITTEN" cols="12">
        <v-checkbox
          :model-value="hasMercuryOptionActivated"
          class="mt-0"
          density="compact"
          hide-details
          :label="$t('project.signing-checklist.add.step2.Section2.mercuryCaseLabel')"
          @update:model-value="mercuryOptionChange"
        />
      </v-col>
    </v-row>

    <SignerOrderSignatureTypeIncompatibilityWarningDialog
      v-if="signerOrderSignatureTypeIncompatibilityWarningDialog"
      @close="signerOrderSignatureTypeIncompatibilityWarningDialog = false"
      @confirm="confirmChangeSignatureType"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'
import { mapGetters, mapMutations, mapState } from 'vuex'

import AppDownloadFile from '@/common/AppDownloadFile.vue'
import { formatFileSize } from '@/common/utils/files'
import { formFieldErrors, VuelidateFormField } from '@/common/utils/form'
import { BtoMB } from '@/common/utils/sizes'
import type { TileOption } from '@/design-system/tiles/types'
import {
  ENVELOPE_TYPES,
  SIGNATURE_TYPE_DISPLAY_ORDER,
  SIGNATURE_TYPE_DOCUSIGN_QUALIFIED,
  SIGNATURE_TYPE_HANDWRITTEN,
  SIGNATURE_TYPE_WITH_AUTH,
} from '@/project/signing-checklist/constants'
import SignerOrderSignatureTypeIncompatibilityWarningDialog from '@/project/signing-checklist/dialogs/SignerOrderSignatureTypeIncompatibilityWarningDialog.vue'
import { SET_IMPORTED_FILE_MERCURY_OPTION } from '@/store/modules/signing-checklist/mutation_types'

type EnvelopeTypeOption = {
  disabled: boolean
  providerMaxSize: number
  text: string
  value: string
}

type DataType = {
  SIGNATURE_TYPE_HANDWRITTEN: typeof SIGNATURE_TYPE_HANDWRITTEN
  envelopeTypeOptions: EnvelopeTypeOption[]
  signerOrderSignatureTypeIncompatibilityWarningDialog: boolean
}

export default defineComponent({
  name: 'Section2',
  components: {
    AppDownloadFile,
    SignerOrderSignatureTypeIncompatibilityWarningDialog,
  },
  props: {
    isEditingLimited: {
      type: Boolean,
      required: true,
    },
    signatureTypeFormField: {
      type: Object as PropType<VuelidateFormField>,
      required: true,
    },
    fileId: {
      type: Number,
      required: true,
    },
    fileSize: {
      type: Number,
      required: true,
    },
    hasSignersOrder: {
      type: Boolean,
      required: true,
    },
    hasMercuryOptionActivated: {
      type: Boolean,
      required: true,
    },
    envelopeType: {
      type: String,
      required: true,
    },
  },
  data (): DataType {
    return {
      SIGNATURE_TYPE_HANDWRITTEN,
      signerOrderSignatureTypeIncompatibilityWarningDialog: false,
      envelopeTypeOptions: [],
    }
  },
  computed: {
    ...mapState('signingChecklist', ['qualifiedDSWarningIsMuted']),
    ...mapState('room', ['currentRoom', 'roomProvidersPending']),
    ...mapGetters('room', ['isWitnessEnabled']),
    learnMoreSignatureTypesLink (): string {
      const locale = this.$i18n.locale as string
      return `${import.meta.env.VUE_APP_API_URL}/assets/pdf?file=signatures-types-${locale.toUpperCase()}`
    },
    signatureTypeErrorMessages (): Array<string> {
      return formFieldErrors(
        this.signatureTypeFormField,
        [
          ['required', this.$t('common.validations.fieldRequired', { fieldName: this.$t('project.signing-checklist.add.step2.Section2.signatureTypeLabel') })],
        ],
      )
    },
    textTooltipDisabledOption (): string {
      return this.envelopeType === ENVELOPE_TYPES.MULTIPLE ? 'envelopeMultipleDSQOptionDisabledTooltip' : 'envelopeTypeOptionDisabledTooltip'
    },
    signatureTypeTileOptions (): TileOption[] {
      return this.envelopeTypeOptions.map(option => ({
        value: option.value,
        label: option.text,
        caption: this.getTileCaption(option),
        disabled: option.disabled,
        tooltip: option.disabled
          ? this.$t(`project.signing-checklist.add.step2.Section2.${this.textTooltipDisabledOption}`)
          : undefined,
        dataLocator: `${option.value.split('.').join('-')}-signature-type-card`,
      }))
    },
  },
  methods: {
    ...mapMutations('signingChecklist', [SET_IMPORTED_FILE_MERCURY_OPTION]),
    signatureTypeChange (signatureType: string | null) {
      const shouldWarnAboutManualSignature = signatureType === SIGNATURE_TYPE_HANDWRITTEN && this.hasSignersOrder
      if (shouldWarnAboutManualSignature) {
        this.signerOrderSignatureTypeIncompatibilityWarningDialog = true
        return
      }
      const shouldWarnAboutQualifiedSignature = signatureType === SIGNATURE_TYPE_DOCUSIGN_QUALIFIED && !this.qualifiedDSWarningIsMuted
      if (shouldWarnAboutQualifiedSignature) {
        this.$emit('setQualifiedDSWarningDialog', true)
        this.$emit('setSelectSignatureType', this.signatureTypeFormField.$model)
      } else {
        this.$emit('setSelectSignatureType', signatureType)
      }
    },
    confirmChangeSignatureType () {
      this.signerOrderSignatureTypeIncompatibilityWarningDialog = false
      this.$emit('setSelectSignatureType', SIGNATURE_TYPE_HANDWRITTEN)
    },
    getProviderMaxSizeInBytes (providerMaxSize: number): number {
      // TODO ask the back to improve that
      return providerMaxSize * 1048576
    },
    getTileCaption (option: EnvelopeTypeOption): string {
      const parts: string[] = []
      if (SIGNATURE_TYPE_WITH_AUTH.includes(option.value)) {
        parts.push(this.$t('project.signing-checklist.add.step2.Section2.signingTypeAuthRequired'))
      }
      if (option.value === SIGNATURE_TYPE_HANDWRITTEN) {
        parts.push(this.$t('project.signing-checklist.add.step2.Section2.signingTypeNoOrder'))
      }
      parts.push(this.$t(
        'project.signing-checklist.add.step2.Section2.signingTypeMaxSize',
        [formatFileSize(this.getProviderMaxSizeInBytes(option.providerMaxSize))],
      ))
      return parts.join(' ')
    },
    mercuryOptionChange (value: boolean | null) {
      this[SET_IMPORTED_FILE_MERCURY_OPTION]({
        fileId: this.fileId,
        value: value,
      })
    },
    getEnvelopesTypeOptions () {
      const enveloppes: Record<string, EnvelopeTypeOption> = {}

      if (!this.roomProvidersPending && this.currentRoom.providers) {
        for (const provider of this.currentRoom.providers) {
          for (const type of provider.types) {
            const value = provider.provider + '.' + type
            const isDisabled = this.envelopeType === ENVELOPE_TYPES.MULTIPLE && value === SIGNATURE_TYPE_DOCUSIGN_QUALIFIED
            enveloppes[value] = {
              text: this.$t(`project.signing-checklist.add.step2.Section2.signingTypeSelect.${value}`),
              value,
              disabled: this.fileSize > provider.maxSize || isDisabled,
              providerMaxSize: BtoMB(provider.maxSize),
            }
          }
        }
      }

      this.envelopeTypeOptions = SIGNATURE_TYPE_DISPLAY_ORDER
        .map(signatureType => enveloppes[signatureType])
        .filter(e => e !== undefined)
    },

  },
  mounted () {
    this.getEnvelopesTypeOptions()
  },
  watch: {
    envelopeType (newValue) {
      this.envelopeTypeOptions = this.envelopeTypeOptions.map(sig =>
      sig.value === SIGNATURE_TYPE_DOCUSIGN_QUALIFIED ? { ...sig, disabled: newValue === ENVELOPE_TYPES.MULTIPLE } : sig,
      )
    },
  },
})
</script>

